import React, { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack'; 
import { getActivityList, saveSalesActivity, deleteSalesActivity } from '../../lib/api/salesActivity'; 
import { Box } from '@mui/material';
import SalesActivityLog from '../../components/salesActivity/SalesActivityLog';

// 🚀 부모로부터 토글 함수와 상태값을 받아와 연동합니다.
const SalesActivityContainer = ({ toggleCalFull, isCalFull }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isSaving, setIsSaving] = useState(false);
    const [events, setEvents] = useState([]);

    // 🚀 [구조 보완] 실제 작동할 currentDate 상태와 상태 변경 함수를 오늘 날짜 기준으로 온전히 선언합니다.
    const [currentDate, setCurrentDate] = useState(new Date());

    // 🚀 [Helper 함수] Date 객체를 "YYYY-MM-DD" 포맷 문자열로 변환하는 함수
    const formatDateToYMD = (dateObj) => {
        if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
            return '';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // =================================================================
    // 🚀 [onDateChange 함수 구현] 하위 로그/달력 컴포넌트에서 호출할 날짜 변경 함수
    // =================================================================
    const onDateChange = useCallback((newDate) => {
        if (!newDate || !(newDate instanceof Date) || isNaN(newDate.getTime())) {
            return;
        }
        console.log("📅 달력/타임라인 날짜 변경 감지:", newDate);
        setCurrentDate(newDate); // 최상위 날짜 상태를 업데이트 ➡️ useEffect를 트리거함
    }, []);

    // 🔍 백엔드에서 데이터를 직접 조회해 오는 함수
    // 💡 [오류 수정]: 함수 재생성으로 인한 무한 루프를 막기 위해 의존성 배열에서 currentDate를 제거합니다.
    const fetchInitialData = useCallback(async (targetDateObj) => {
        try {
            const localUser = localStorage.getItem('user');
            let usrUuid = '';

            if (localUser) {
                const parsedUser = JSON.parse(localUser);
                usrUuid = parsedUser.usrUuid || parsedUser.userUuid || '';
            }

            const currentPath = window.location.pathname;
            let viewType = 'TODAY'; 

            if (currentPath === '/salesActivity') {
                viewType = 'ALL';
            } else {
                usrUuid = '';
            }

            // 🚀 인자로 전달받은 날짜 객체(가장 최신 변경 값)를 기반으로 포맷팅을 수행합니다.
            const targetDate = formatDateToYMD(targetDateObj || new Date());
            
            const response = await getActivityList(usrUuid, targetDate, viewType);
            
            if (response.data && response.data.list && response.data.list.length > 0) {
                const mappedEvents = response.data.list.map(item => ({
                    id: item.activityId, 
                    title: item.title,
                    start: item.startDatetime, 
                    end: item.endDatetime,
                    backgroundColor: '#1976d2', 
                    borderColor: '#1976d2',
                    allDay: false, 
                    extendedProps: {
                        companyUuid: item.companyUuid,
                        content: item.activityContent,
                        usrUuid: item.usrUuid,
                        usrNm: item.usrNm,
                        meetPlace: item.meetPlace,
                        companyNm: item.companyNm,
                    }
                }));
                setEvents(mappedEvents);
            } else {
                setEvents([]);
            }
        } catch (err) {
            console.error('영업활동 목록 로드 오류:', err);
            const message = err.response?.data?.message || err.message || '목록을 불러오는 중 오류가 발생했습니다.';
            enqueueSnackbar(message, { variant: 'error' });
        }
    }, [enqueueSnackbar]); // 🔒 currentDate 의존성을 완전히 끊어 함수를 고정시킵니다.

    // 🔒 [실시간 동기화 라인 변경] 
    // 오직 currentDate 상태가 변경될 때만 fetchInitialData가 가장 최신 날짜를 들고 백엔드를 호출합니다.
    useEffect(() => {
        fetchInitialData(currentDate);
    }, [fetchInitialData, currentDate]); // 🚀 날짜 이동 버튼 클릭 시 이 useEffect가 유기적으로 작동합니다.




    const onSave = async (formData) => {
        setIsSaving(true);
        try {
            const startDatetime = `${formData.startDate} ${formData.startTime}:00`;
            const endDatetime = `${formData.endDate} ${formData.endTime}:00`;

            const finalData = {
                activityId: formData.activityId || null,
                title: formData.title,
                companyUuid: formData.companyUuid,
                meetPlace : formData.meetPlace,
                activityContent: formData.content,                
                startDatetime: startDatetime,
                endDatetime: endDatetime,
                usrUuid: formData.usrUuid || ''
            };

            const response = await saveSalesActivity(finalData);
            if (response.data.result === 'success') {
                enqueueSnackbar('성공적으로 저장되었습니다.', { variant: 'success' });
                fetchInitialData();
            }
        } catch (err) {
            console.error('저장 오류:', err);
            const message = err.response?.data?.message || err.message || '저장 중 오류가 발생했습니다.';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const onDelete = async (activityId) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        setIsSaving(true);
        try {
            const response = await deleteSalesActivity({ activityId });
            if (response.data.result === 'success') {
                enqueueSnackbar('성공적으로 삭제되었습니다.', { variant: 'success' });
                fetchInitialData();
            }
        } catch (err) {
            console.error('삭제 오류:', err);
            const message = err.response?.data?.message || err.message || '삭제 중 오류가 발생했습니다.';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleEventClick = (clickInfo) => {
        console.log('클릭한 이벤트 정보:', clickInfo.event);
    };

    const btnStyle = { 
    display: 'flex',          // 🛠️ 추가: 아이콘 중앙 배치를 위한 플렉스박스 가동
    alignItems: 'center',     // 🛠️ 추가: 세로 중앙 정렬
    justifyContent: 'center',  // 🛠️ 추가: 가로 중앙 정렬
    padding: '8px',           // 🛠️ 교정: 아이콘 크기에 맞춰 사방 여백 통일
    cursor: 'pointer', 
    backgroundColor: '#fff', 
    border: '1px solid #ccc', 
    borderRadius: '4px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    transition: 'all 0.2s',
    '&:hover': {              // 🛠️ 추가: 마우스 올렸을 때 살짝 강조 효과
        backgroundColor: '#f5f5f5',
        borderColor: '#999'
    }
};

    return (
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
          {window.location.pathname !== '/salesActivity' && (
              <Box 
                  sx={{ 
                      position: 'absolute', 
                      top: '6px', 
                      right: '16px', 
                      zIndex: 100 
                  }}
              >
                  <button 
                      onClick={toggleCalFull} 
                      style={btnStyle}
                      title={isCalFull ? "원래 크기로 축소" : "화면 가득 확장"}
                  >
                      {isCalFull ? (
                          /* ↙↘ 축소 아이콘 이미지 */
                          <svg xmlns="http://w3.org" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                              <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M10 14l-7 7"/>
                          </svg>
                      ) : (
                          /* ↖↗ 확장 아이콘 이미지 */
                          <svg xmlns="http://w3.org" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                          </svg>
                      )}
                  </button>
              </Box>
          )}
            <SalesActivityLog 
                events={events} 
                currentDate={currentDate}
                onDateChange={setCurrentDate}
                handelEventClick={handleEventClick}
                onSaveActivity={onSave}
                onDeleteActivity={onDelete}
                
                // 💡 화면이 가로로 넓어졌을 때는 등록/수정이 가능하도록 권한을 열어주는 UI 바인딩 속성은 그대로 유지합니다.
                isLocationValid={isCalFull || window.location.pathname === '/salesActivity'} 
                isCalFull={isCalFull} 
            />
        </Box>
    );
};

const btnStyle = { 
    padding: '6px 12px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', 
    backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
};

export default SalesActivityContainer;
